// /analysis — lista de páginas de análisis personalizado.

import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaTimes, FaLayerGroup } from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButton,
  TechInput,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { listPages, createPage } from '../../services/analysisService';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.9rem;
`;

const Card = styled(Link)`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 4px solid ${({ $color }) => $color || accent};
  border-radius: 10px;
  padding: 1rem 1.1rem;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  color: inherit;
  min-height: 130px;
  transition: all 0.18s ease;
  &:hover {
    border-color: rgba(0, 180, 216, 0.5);
    background: rgba(0, 180, 216, 0.04);
    transform: translateY(-1px);
  }
  .title { color: var(--text-primary); font-weight: 600; font-size: 0.98rem; }
  .desc {
    color: var(--text-secondary); font-size: 0.83rem; line-height: 1.45;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .meta {
    margin-top: auto; font-family: ${mono};
    font-size: 0.6rem; letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1400;
  background: rgba(8, 14, 24, 0.7); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
`;
const Modal = styled.div`
  width: min(560px, 100%);
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  padding: 1.3rem 1.4rem;
  position: relative;
  h2 { color: var(--text-primary); margin: 0 0 0.85rem; }
  label {
    display: block; font-family: ${mono};
    font-size: 0.62rem; letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--text-disabled); margin: 0.7rem 0 0.3rem;
  }
  textarea {
    width: 100%; min-height: 70px;
    background: rgba(11, 19, 32, 0.65);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    font-family: inherit;
    padding: 0.55rem 0.75rem;
    font-size: 0.88rem; outline: none; resize: vertical;
    &:focus { border-color: rgba(0, 180, 216, 0.5); }
  }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { color: var(--alert-error); }
  }
  .actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.1rem; }
`;

const COVERS = ['#00b4d8', '#06d6a0', '#f4a261', '#ef476f', '#a78bfa', '#fbbf24'];

const TEMPLATES = [
  { title: 'Mapa de poder bonaerense', description: 'Análisis multinivel: SN → BA → AR. Pasos: agregar actores clave y dejar que la IA scrapee conexiones.', cover_color: '#a78bfa' },
  { title: 'Bloque LLA · radiografía', description: 'Concejales, votaciones, alianzas, vulnerabilidades.', cover_color: '#00b4d8' },
  { title: 'Posicionamiento Chouhy próximas 4 semanas', description: 'Cronograma + temas + mensajes + indicadores.', cover_color: '#06d6a0' },
];

function NewPageModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contextBrief, setContextBrief] = useState('');
  const [color, setColor] = useState(COVERS[0]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const applyTpl = (t) => {
    setTitle(t.title);
    setDescription(t.description);
    setColor(t.cover_color);
  };

  const submit = async () => {
    if (!title.trim()) { setErr('Título requerido'); return; }
    setSaving(true); setErr(null);
    try {
      const p = await createPage({
        title: title.trim(),
        description: description.trim() || null,
        context_brief: contextBrief.trim() || null,
        cover_color: color,
      });
      onCreated(p);
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setSaving(false); }
  };

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2><FaLayerGroup style={{ marginRight: 8, color: accent }} /> Nueva página de análisis</h2>
        <label>Plantilla (opcional)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          {TEMPLATES.map((t) => (
            <button key={t.title} type="button" onClick={() => applyTpl(t)}
              style={{ background: 'transparent', border: '1px solid ' + borderSoft, color: 'var(--text-secondary)',
                padding: '5px 10px', borderRadius: 999, fontSize: '0.74rem', cursor: 'pointer' }}>
              {t.title}
            </button>
          ))}
        </div>
        <label>Título</label>
        <TechInput autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder='Ej: "Análisis red Passaglia + LLA bonaerense"' />
        <label>Descripción</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        <label>Brief de contexto (lo que el chat IA siempre verá)</label>
        <textarea value={contextBrief} onChange={(e) => setContextBrief(e.target.value)}
          placeholder='Ej: "Foco: identificar vulnerabilidades del bloque oficialista para campaña 2027"' />
        <label>Color</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {COVERS.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)}
              style={{ width: 28, height: 28, borderRadius: 6, background: c,
                border: color === c ? '2px solid var(--text-primary)' : '1px solid ' + borderSoft, cursor: 'pointer' }}
              aria-label={c} />
          ))}
        </div>
        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}
        <div className="actions">
          <TechButton onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--text-secondary)' }}>Cancelar</TechButton>
          <TechButton onClick={submit} disabled={saving || !title.trim()}>{saving ? '…' : 'Crear'}</TechButton>
        </div>
      </Modal>
    </Backdrop>
  );
}

export default function AnalysisList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    listPages().then(setPages).catch((e) => setErr(friendlyError(e))).finally(() => setLoading(false));
  }, []);

  return (
    <TechShell>
      <TechPageHeader
        title="Análisis personalizado"
        subtitle="Tableros con módulos editables (referencias, conexiones, líneas de tiempo, estadísticas…) y un chat con IA al costado que crea, edita y busca sobre la información del Concejo y fuentes externas."
        dossier="ANALYSIS-LAB"
        actions={<TechButton onClick={() => setShowNew(true)}><FaPlus style={{ marginRight: 6 }} /> Nueva</TechButton>}
      />
      <TechSection title={`${pages.length} páginas`}>
        {loading ? <Loading>Cargando…</Loading>
          : err ? <ErrorBox>{err}</ErrorBox>
          : pages.length === 0 ? <Empty>Sin páginas todavía. Creá la primera arriba.</Empty>
          : (
            <Grid>
              {pages.map((p) => (
                <Card key={p.id} to={`/analysis/${p.slug}`} $color={p.cover_color}>
                  <div className="title">{p.title}</div>
                  {p.description && <div className="desc">{p.description}</div>}
                  <div className="meta">{p.status} · actualizado {new Date(p.updated_at).toLocaleDateString('es-AR')}</div>
                </Card>
              ))}
            </Grid>
          )
        }
      </TechSection>
      {showNew && <NewPageModal onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); navigate(`/analysis/${p.slug}`); }} />}
    </TechShell>
  );
}
